/**
 * safeFetch — a thin wrapper around `fetch()` that sidesteps Android WebView
 * quirks where calling `response.text()` / `.json()` can throw "body stream
 * already read" on certain 4xx responses (particularly 400s), rapid double-taps,
 * or when a Cloudflare-injected `set-cookie` confuses the WebView's body
 * buffering.
 *
 * Usage:
 *   const { ok, status, data, text } = await safeFetch(url, init);
 *   if (!ok) setError(data?.detail || text || `Server returned ${status}`);
 *
 * We always return a PARSED object + raw text so callers never need to touch
 * Response.body themselves.
 *
 * Strategy (in order of preference, each wrapped in its own try):
 *   1. `res.clone().arrayBuffer()` → TextDecoder('utf-8') — most reliable on
 *      Android WebView; doesn't share the consumable body stream.
 *   2. `res.clone().text()` — the original path, works on modern Chrome.
 *   3. `res.text()` — final fallback (loses the body if anything later tries
 *      to read it, but we don't).
 */
export async function safeFetch(url, init) {
  // Network resilience for cellular Indian users:
  //   • Up to 4 attempts with exponential backoff (0.4s, 1s, 2.2s, 4.6s).
  //     This masks the typical VoLTE→4G handoff blip (1-3 s) and the morning
  //     check-in burst when the ingress is briefly saturated.
  //   • Per-attempt 12 s timeout via AbortController, so a hung TLS handshake
  //     fails fast instead of waiting forever for the OS to time out (~60 s
  //     on Android WebView).
  //   • We do NOT retry on HTTP responses (4xx/5xx) — those are server-side
  //     decisions for the caller to handle.
  const MAX_ATTEMPTS = 4;
  const BACKOFFS_MS = [400, 1000, 2200, 4600];
  const PER_ATTEMPT_TIMEOUT_MS = 12_000;

  let res = null;
  let netErr = null;
  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
    const ctrl = (typeof AbortController !== 'undefined') ? new AbortController() : null;
    const timer = ctrl ? setTimeout(() => ctrl.abort(), PER_ATTEMPT_TIMEOUT_MS) : null;
    try {
      const fetchInit = ctrl ? { ...(init || {}), signal: ctrl.signal } : (init || {});
      res = await fetch(url, fetchInit);
      netErr = null;
      break;
    } catch (e) {
      netErr = e;
      // Wait before next attempt — but don't sleep after the last failure.
      if (attempt < MAX_ATTEMPTS - 1) {
        await new Promise(r => setTimeout(r, BACKOFFS_MS[attempt]));
      }
    } finally {
      if (timer) clearTimeout(timer);
    }
  }
  if (!res) {
    return { ok: false, status: 0, data: null, text: '', error: netErr, offline: true };
  }

  let bodyText = '';

  // Path 1 — arrayBuffer + manual decode. Avoids the WebView's body-stream
  // quirk on 4xx responses with set-cookie headers.
  try {
    const buf = await res.clone().arrayBuffer();
    if (buf && buf.byteLength > 0) {
      bodyText = new TextDecoder('utf-8').decode(buf);
    }
  } catch (_) { /* fall through */ }

  // Path 2 — clone + text() (legacy good path).
  if (!bodyText) {
    try { bodyText = await res.clone().text(); } catch (_) { /* fall through */ }
  }

  // Path 3 — last-ditch.
  if (!bodyText) {
    try { bodyText = await res.text(); } catch (_) { bodyText = ''; }
  }

  let data = null;
  if (bodyText) {
    try { data = JSON.parse(bodyText); } catch { /* non-JSON response */ }
  }

  // Fallback: if the WebView ate the response body (a known Android quirk on
  // 4xx with set-cookie), the server still echoes the error message in the
  // ``X-Error-Detail`` header. Hoist it into ``data.detail`` so the existing
  // ``extractError`` path Just Works.
  if (!res.ok && (!data || !data.detail)) {
    try {
      const hdr = res.headers.get('X-Error-Detail');
      if (hdr) {
        data = data || {};
        data.detail = hdr;
      }
    } catch {}
  }

  return { ok: res.ok, status: res.status, data, text: bodyText, error: null, offline: false };
}

/**
 * Convenience — pulls the most-helpful error message out of a safeFetch
 * result. Priority:
 *   1. `result.data.detail` (FastAPI's standard error key)
 *   2. `result.data.message` / `result.data.error`
 *   3. raw response text if it looks like a sentence (≤200 chars, no `{` / `<`)
 *   4. `${fallback} (status)` — always includes status so the user can quote it
 */
export function extractError(result, fallback = 'Something went wrong') {
  if (result.offline) {
    // Distinguish between *device* offline and *server unreachable*.
    // Some Android Capacitor WebViews report navigator.onLine = false even
    // when the SIM has 5G, so we trust the browser only when it confirms
    // we're online. If it does, blame the network instead of the user.
    const browserOnline = typeof navigator !== 'undefined' ? navigator.onLine : true;
    if (browserOnline) {
      return 'Network is slow right now. Please tap the button again in a few seconds.';
    }
    return 'You appear to be offline. Wait for signal and try again.';
  }
  const d = result.data;
  if (d) {
    if (typeof d.detail === 'string') return d.detail;
    if (Array.isArray(d.detail) && d.detail[0]?.msg) return d.detail.map(x => x.msg).join('; ');
    if (typeof d.message === 'string') return d.message;
    if (typeof d.error === 'string') return d.error;
  }
  const t = (result.text || '').trim();
  if (t && t.length < 200 && !t.startsWith('{') && !t.startsWith('<')) return t;
  return `${fallback} (${result.status || 'no response'})`;
}
